import React, { useEffect, useMemo, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { Filter, Download, DollarSign, Users, ClipboardList, RefreshCcw,
         Ban, Wrench, Wallet } from "lucide-react";
import { api } from "@/lib/api";
import { useAdminUi } from "@/context/AdminUiContext";

function KPI({ label, value, tone = "teal", testid }) {
  const tones = {
    teal:  "text-teal-300",
    blue:  "text-blue-300",
    amber: "text-amber-300",
    red:   "text-red-300",
    slate: "text-slate-100",
  };
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4" data-testid={testid}>
      <div className="text-[10px] font-mono uppercase tracking-[0.24em] text-slate-400">{label}</div>
      <div className={`font-display font-extrabold text-2xl mt-1 ${tones[tone]}`}>{value}</div>
    </div>
  );
}

const usd = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const CATS = ["", "home", "commercial", "car", "pet", "outdoor", "convenience"];

export default function AdminReports() {
  const { openUser, openBooking } = useAdminUi();
  const [filters, setFilters] = useState({ from: "", to: "", category: "", cleaner_id: "", customer_id: "" });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cleaners, setCleaners] = useState([]);
  const [customers, setCustomers] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const qs = Object.entries(filters).filter(([, v]) => v).map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join("&");
      const { data } = await api.get(`/admin/reports/overview${qs ? "?" + qs : ""}`);
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load report");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    (async () => {
      try {
        const [cl, cu] = await Promise.all([
          api.get("/admin/cleaners"),
          api.get("/admin/customers"),
        ]);
        setCleaners(cl.data || []);
        setCustomers(cu.data || []);
      } catch (_) { /* not fatal */ }
    })();
    load();
    // eslint-disable-next-line
  }, []);

  const downloadCsv = async () => {
    try {
      const qs = Object.entries(filters).filter(([, v]) => v).map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join("&");
      const resp = await api.get(`/admin/reports/export.csv${qs ? "?" + qs : ""}`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url; a.download = "cleanbaz-report.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Export failed"); }
  };

  const k = data?.kpi || {};
  const maxDay = useMemo(() => {
    const s = data?.time_series || [];
    return s.reduce((m, r) => Math.max(m, r.revenue || 0, r.bookings || 0), 1);
  }, [data]);

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8 text-slate-100" data-testid="admin-reports-page">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-teal-300">Admin · Reports</div>
          <h1 className="font-display font-extrabold text-2xl mt-1">Business intelligence</h1>
          <p className="text-sm text-slate-400 mt-1">Real database aggregates — no demo numbers. Filter and export.</p>
        </div>
        <button onClick={downloadCsv} data-testid="reports-export-csv" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-teal-400/40 text-teal-200 hover:bg-teal-500/10 text-xs font-mono uppercase tracking-[0.16em]">
          <Download className="w-3.5 h-3.5" /> Export CSV
        </button>
      </div>

      {/* Filters */}
      <Card className="mb-6 bg-white/[0.03] border-white/10">
        <CardContent className="p-4 grid grid-cols-2 md:grid-cols-6 gap-3 items-end">
          <div>
            <label className="text-[10px] font-mono uppercase tracking-widest text-slate-400">From</label>
            <input type="date" value={filters.from} onChange={(e) => setFilters({ ...filters, from: e.target.value })} data-testid="reports-filter-from" className="mt-1 w-full bg-slate-900 border border-white/10 rounded-md px-2 py-1.5 text-sm text-slate-100" />
          </div>
          <div>
            <label className="text-[10px] font-mono uppercase tracking-widest text-slate-400">To</label>
            <input type="date" value={filters.to} onChange={(e) => setFilters({ ...filters, to: e.target.value })} data-testid="reports-filter-to" className="mt-1 w-full bg-slate-900 border border-white/10 rounded-md px-2 py-1.5 text-sm text-slate-100" />
          </div>
          <div>
            <label className="text-[10px] font-mono uppercase tracking-widest text-slate-400">Service</label>
            <select value={filters.category} onChange={(e) => setFilters({ ...filters, category: e.target.value })} data-testid="reports-filter-category" className="mt-1 w-full bg-slate-900 border border-white/10 rounded-md px-2 py-1.5 text-sm text-slate-100">
              {CATS.map((c) => <option key={c} value={c}>{c || "All categories"}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] font-mono uppercase tracking-widest text-slate-400">Cleaner</label>
            <select value={filters.cleaner_id} onChange={(e) => setFilters({ ...filters, cleaner_id: e.target.value })} data-testid="reports-filter-cleaner" className="mt-1 w-full bg-slate-900 border border-white/10 rounded-md px-2 py-1.5 text-sm text-slate-100">
              <option value="">All cleaners</option>
              {cleaners.map((c) => <option key={c.user_id} value={c.user_id}>{c.user?.full_name || c.user?.email}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] font-mono uppercase tracking-widest text-slate-400">Customer</label>
            <select value={filters.customer_id} onChange={(e) => setFilters({ ...filters, customer_id: e.target.value })} data-testid="reports-filter-customer" className="mt-1 w-full bg-slate-900 border border-white/10 rounded-md px-2 py-1.5 text-sm text-slate-100">
              <option value="">All customers</option>
              {customers.map((c) => <option key={c.id} value={c.id}>{c.full_name || c.email}</option>)}
            </select>
          </div>
          <button onClick={load} data-testid="reports-apply" className="h-9 rounded-md bg-teal-500 hover:bg-teal-400 text-slate-950 text-xs font-mono uppercase tracking-[0.16em] font-semibold inline-flex items-center justify-center gap-1.5">
            <Filter className="w-3.5 h-3.5" /> Apply
          </button>
        </CardContent>
      </Card>

      {loading || !data ? (
        <div className="text-slate-400">Loading real numbers from the database…</div>
      ) : (
        <>
          {/* KPI grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <KPI label="Total revenue"       value={usd(k.total_revenue)}         tone="teal"  testid="kpi-total-revenue" />
            <KPI label="Gross booking volume" value={usd(k.gross_booking_volume)} tone="teal"  testid="kpi-gmv" />
            <KPI label="Platform revenue (20%)" value={usd(k.platform_revenue)}   tone="teal"  testid="kpi-platform-revenue" />
            <KPI label="Cleaner payouts (80%)"  value={usd(k.cleaner_payouts)}    tone="blue"  testid="kpi-cleaner-payouts" />
            <KPI label="Refunds"              value={usd(k.refunds)}              tone="amber" testid="kpi-refunds" />
            <KPI label="Cancellations"        value={k.cancellations || 0}        tone="red"   testid="kpi-cancellations" />
            <KPI label="Completed jobs"       value={k.completed_jobs || 0}       tone="teal"  testid="kpi-completed" />
            <KPI label="Pending jobs"         value={k.pending_jobs || 0}         tone="amber" testid="kpi-pending" />
            <KPI label="Active jobs"          value={k.active_jobs || 0}          tone="teal"  testid="kpi-active" />
            <KPI label="Total jobs (filter)"  value={k.total_jobs || 0}           tone="slate" testid="kpi-total-jobs" />
            <KPI label="Customer count"       value={k.customer_count || 0}       tone="slate" testid="kpi-customers" />
            <KPI label="Cleaner count"        value={k.cleaner_count || 0}        tone="slate" testid="kpi-cleaners" />
          </div>

          {/* Time series */}
          <Card className="mb-6 bg-white/[0.03] border-white/10">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="text-sm font-display font-bold text-slate-100">Revenue & bookings over time</div>
                <div className="text-[10px] font-mono uppercase text-slate-400">{data.time_series?.length || 0} days</div>
              </div>
              {data.time_series?.length === 0 ? (
                <div className="text-slate-500 text-sm">No data in this range.</div>
              ) : (
                <div className="flex items-end gap-1 overflow-x-auto pb-2" data-testid="reports-timeseries">
                  {data.time_series.map((r) => (
                    <div key={r.date} className="flex-shrink-0 w-6 flex flex-col items-center gap-1" title={`${r.date}\n$${r.revenue.toFixed(2)} · ${r.bookings} bookings`}>
                      <div className="w-full bg-teal-400/70 rounded-t" style={{ height: `${Math.round(80 * (r.revenue / (maxDay || 1)))}px`, minHeight: "1px" }} />
                      <div className="w-full bg-blue-400/50 rounded-b" style={{ height: `${Math.round(30 * (r.bookings / (maxDay || 1)))}px`, minHeight: "1px" }} />
                      <div className="text-[9px] font-mono text-slate-400 rotate-45 origin-top-left mt-1">{r.date.slice(5)}</div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* By category */}
          <div className="grid md:grid-cols-2 gap-4 mb-6">
            <Card className="bg-white/[0.03] border-white/10">
              <CardContent className="p-4">
                <div className="text-sm font-display font-bold text-slate-100 mb-3">Bookings by category</div>
                <table className="w-full text-xs" data-testid="reports-by-category">
                  <thead className="text-[10px] font-mono uppercase text-slate-400 border-b border-white/10">
                    <tr><th className="text-left p-2">Category</th><th className="text-right p-2">Jobs</th><th className="text-right p-2">Revenue</th></tr>
                  </thead>
                  <tbody>
                    {data.by_category.map((r) => (
                      <tr key={r.category} className="border-t border-white/5">
                        <td className="p-2 text-slate-200">{r.category}</td>
                        <td className="p-2 text-right font-mono text-slate-100">{r.jobs}</td>
                        <td className="p-2 text-right font-mono text-teal-300">{usd(r.revenue)}</td>
                      </tr>
                    ))}
                    {data.by_category.length === 0 && <tr><td colSpan={3} className="p-6 text-center text-slate-500">No jobs.</td></tr>}
                  </tbody>
                </table>
              </CardContent>
            </Card>
            <Card className="bg-white/[0.03] border-white/10">
              <CardContent className="p-4">
                <div className="text-sm font-display font-bold text-slate-100 mb-3">Bookings by exact service</div>
                <table className="w-full text-xs" data-testid="reports-by-service">
                  <thead className="text-[10px] font-mono uppercase text-slate-400 border-b border-white/10">
                    <tr><th className="text-left p-2">Service</th><th className="text-right p-2">Jobs</th><th className="text-right p-2">Revenue</th></tr>
                  </thead>
                  <tbody>
                    {data.by_service.slice(0, 20).map((r) => (
                      <tr key={r.service} className="border-t border-white/5">
                        <td className="p-2 text-slate-200">{r.service}</td>
                        <td className="p-2 text-right font-mono text-slate-100">{r.jobs}</td>
                        <td className="p-2 text-right font-mono text-teal-300">{usd(r.revenue)}</td>
                      </tr>
                    ))}
                    {data.by_service.length === 0 && <tr><td colSpan={3} className="p-6 text-center text-slate-500">No jobs.</td></tr>}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </div>

          {/* Cleaner performance */}
          <Card className="mb-6 bg-white/[0.03] border-white/10">
            <CardContent className="p-4">
              <div className="text-sm font-display font-bold text-slate-100 mb-3">Cleaner performance</div>
              <table className="w-full text-xs" data-testid="reports-cleaner-performance">
                <thead className="text-[10px] font-mono uppercase text-slate-400 border-b border-white/10">
                  <tr>
                    <th className="text-left p-2">Cleaner</th>
                    <th className="text-right p-2">Paid jobs</th>
                    <th className="text-right p-2">Avg rating</th>
                    <th className="text-right p-2">Payouts</th>
                  </tr>
                </thead>
                <tbody>
                  {data.cleaner_performance.map((r) => (
                    <tr key={r.cleaner_id} className="border-t border-white/5 hover:bg-white/[0.03] cursor-pointer" onClick={() => openUser(r.cleaner_id)} data-testid={`reports-cleaner-${r.cleaner_id}`}>
                      <td className="p-2">
                        <div className="text-slate-100">{r.full_name || "—"}</div>
                        <div className="text-[10px] text-slate-500 font-mono">{r.email}</div>
                      </td>
                      <td className="p-2 text-right font-mono">{r.jobs}</td>
                      <td className="p-2 text-right font-mono text-amber-300">{r.avg_rating ?? "—"}</td>
                      <td className="p-2 text-right font-mono text-blue-300">{usd(r.payouts)}</td>
                    </tr>
                  ))}
                  {data.cleaner_performance.length === 0 && <tr><td colSpan={4} className="p-6 text-center text-slate-500">No cleaner activity in this range.</td></tr>}
                </tbody>
              </table>
            </CardContent>
          </Card>

          {/* Payment history */}
          {data.payments?.length > 0 && (
            <Card className="mb-6 bg-white/[0.03] border-white/10">
              <CardContent className="p-4">
                <div className="text-sm font-display font-bold text-slate-100 mb-3">Recent payment history ({data.payments.length})</div>
                <table className="w-full text-xs" data-testid="reports-payments">
                  <thead className="text-[10px] font-mono uppercase text-slate-400 border-b border-white/10">
                    <tr>
                      <th className="text-left p-2">When</th>
                      <th className="text-left p-2">Booking</th>
                      <th className="text-left p-2">Type</th>
                      <th className="text-left p-2">Status</th>
                      <th className="text-right p-2">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.payments.slice(0, 100).map((p, i) => (
                      <tr key={i} className="border-t border-white/5">
                        <td className="p-2 text-slate-300 font-mono">{p.created_at ? new Date(p.created_at).toLocaleString() : "—"}</td>
                        <td className="p-2 font-mono">
                          {p.job_id ? (
                            <button onClick={() => openBooking(p.job_id)} className="text-teal-300 hover:underline">{p.job_id.slice(0, 8)}</button>
                          ) : "—"}
                        </td>
                        <td className="p-2 text-slate-300">{p.type || "charge"}</td>
                        <td className="p-2 text-slate-300">{p.status}</td>
                        <td className="p-2 text-right font-mono text-slate-100">{usd(p.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
